import React from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { StyledForm } from './ActivityPanelStyles';
import { EMAIL_REG_EXP } from '../../../shared/constants';
import { ActivityFormData } from './types';
import CcRecipientsField from './CcRecipientsField';

const activityFormSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(EMAIL_REG_EXP, 'Wrong email format.'),
  ccRecipients: yup
    .array()
    .of(yup.string().trim().required())
    .test(
      'valid-cc-emails',
      'Wrong email format.',
      value => !value || value.every(email => EMAIL_REG_EXP.test(email)),
    )
    .default([]),
  message: yup
    .string()
    .required('Message is required')
    .max(2000, 'Message must be less than 2000 characters'),
});

interface ActivityFormProps {
  defaultEmail?: string;
  onSubmit: SubmitHandler<ActivityFormData>;
  isLoading?: boolean;
  enableCcRecipients?: boolean;
}

const ActivityForm: React.FC<ActivityFormProps> = ({
  defaultEmail = '',
  onSubmit,
  isLoading = false,
  enableCcRecipients = false,
}) => {
  const [ccFieldInstanceKey, setCcFieldInstanceKey] = React.useState(0);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ActivityFormData>({
    resolver: yupResolver(activityFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      email: defaultEmail,
      ccRecipients: [],
      message: '',
    },
  });

  const onFormSubmit: SubmitHandler<ActivityFormData> = data => {
    onSubmit(data);
    reset({ email: defaultEmail, ccRecipients: [], message: '' });
    setCcFieldInstanceKey(current => current + 1);
  };

  return (
    <StyledForm>
      <Typography variant="h6" component="h2" gutterBottom>
        Activity
      </Typography>

      <Controller
        name="email"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            required
            label="Email"
            variant="outlined"
            fullWidth
            error={!!errors.email}
            helperText={errors.email?.message || ' '}
            margin="normal"
          />
        )}
      />

      {enableCcRecipients && (
        <Controller
          name="ccRecipients"
          control={control}
          render={({ field }) => (
            <CcRecipientsField
              key={ccFieldInstanceKey}
              value={field.value || []}
              onChange={field.onChange}
              errorMessage={errors.ccRecipients?.message}
            />
          )}
        />
      )}

      <Controller
        name="message"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            required
            label="Message"
            placeholder="Write a message here"
            variant="outlined"
            multiline
            minRows={4}
            fullWidth
            error={!!errors.message}
            helperText={errors.message?.message || ' '}
            margin="normal"
          />
        )}
      />

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button
          onClick={handleSubmit(onFormSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting || isLoading}
        >
          SEND
        </Button>
      </Box>
    </StyledForm>
  );
};

export default ActivityForm;
